import cx from "classnames";
import { type ForwardedRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import {
  compareScope,
  type Scope,
  type ScopeController
} from "../PreviewCanvas/usePreviewDocument";
import { DocumentExportAdvanced } from "./DocumentExportAdvanced";
import { DocumentExportGeneral } from "./DocumentExportGeneral";
import { DocumentExportPages } from "./DocumentExportPages";
import { type DisabledData, type ExportHandleRef, ExportModalMode } from "../ExportModal";
import { Exporter } from "../../Exporter";
import { Flex, Tabs } from "@theorgchartdev/storybook";
import { type FormController, useFormController } from "@/hooks/useFormController";
import {
  getDefaultExportName,
  getDefaultPageSize,
  getInitialPageSize,
  getInitialPagination,
  hasInvalidFilenameChars,
  readStoredExportSettings,
  storeExportSettings
} from "../ExportModal.utils";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { getScopeOptions } from "../PreviewCanvas/usePreviewDocument";
import {
  countExportableRecords,
  handlePreviewUpdate,
  handleSetup,
  resetPreview
} from "../PreviewCanvas/PreviewCanvas.utils";
import { NormalizeSCWiz } from "@/appComponents/Navigation/RightSidebar/Optimize/Optimize.utils";
import { PreviewCanvas } from "../PreviewCanvas";
import { useStatusController } from "@/hooks/useStatusController";
import { useStatusListener } from "@/hooks/useStatusListener";
import { Globals } from "@coreapp/CoreApp/Globals";
import { kAccountADPBasic, enumPageOrientationOptions, enumFillMode } from "@coreapp/constants";
import { QXDoc } from "@coreapp/CoreApp/QXDoc";
import { appstr } from "@coreapp/utils/appstr";
import { gBoxLimitKey } from "@coreapp/whitelabel/brand";

type Props = {
  handleRef: ForwardedRef<ExportHandleRef>;
  mode: ExportModalMode;
  setDisabled: (key: string, value: DisabledData) => void;
};

export type FieldData = {
  // General
  name: string;
  scope: Scope;
  pageSize: string;
  pageOrientation: number;
  includeBackground: boolean;
  whiteBackground: boolean;
  showOverlay: boolean;
  showActivity: string;

  // Pagination
  breakevery: string;
  breakfield: string;
  minsubsforbreak: string;
  nameusing: string;
  pagedensity: number;
  scChartBreakType: number;
  startbreaksat: string;

  shouldPreserveSubchartSort: boolean;
  scSortKey: Optional<string>;

  // sortConfig: QSortConfig;

  // Advanced
  navigationArrows: boolean;
  indexPage: boolean;
  uniformScaling: boolean;
  includefilteredmgrs: boolean;
  functionStatements: boolean;
};

export type TabProps = {
  formController: FormController<FieldData>;
  mode: ExportModalMode;
};

export const DocumentExportContent = (props: Props) => {
  const isADPBasicAccount = Globals._accountInfo.GetOC5Edition() === kAccountADPBasic;

  const statusController = useStatusController();
  const exportStartedRef = useRef(false);
  const backgroundRef = useRef({ color: "#FFFFFF", fillMode: enumFillMode.solidFill });
  const sourceDocumentRef = useRef<QXDoc | null>(null);
  const mockDocumentRef = useRef<QXDoc | null>(null);

  const [currentTab, setCurrentTab] = useState(0);
  const scopeControllerRef = useRef<ScopeController>(null);
  const formController = useFormController<FieldData>(
    () => {
      // const sortConfig = doc?.GetLastSort().Clone() ?? new QSortConfig();

      const doc = GetDoc();
      const scWiz = doc?.GetIWizData() ?? {};
      const currentPagination = NormalizeSCWiz(scWiz);

      const defaultSettings = {
        /* General */
        scope: "Branch",
        pageSize: getDefaultPageSize(doc),
        pageOrientation: enumPageOrientationOptions.landscape,
        includeBackground: !isADPBasicAccount,
        whiteBackground: false,
        showOverlay: true,
        showActivity: "Off",
        /* Pagination */
        ...currentPagination,
        shouldPreserveSubchartSort: true,
        scSortKey: undefined,
        // sortConfig,
        /* Advanced */
        navigationArrows: true,
        indexPage: false,
        uniformScaling: false,
        includefilteredmgrs: false,
        functionStatements: false
      } as const;

      const storedSettings = readStoredExportSettings(defaultSettings, "document");

      return {
        name: getDefaultExportName(),
        ...storedSettings,
        ...getInitialPageSize(doc, storedSettings),
        ...getInitialPagination(doc, storedSettings)
      };
    },
    {
      onChange: (nextFieldData: FieldData, updatedKey: keyof FieldData) => {
        if (updatedKey === "name") {
          const isInvalid = hasInvalidFilenameChars(nextFieldData.name);
          props.setDisabled("invalidFilename", {
            isDisabled: isInvalid,
            tooltip: isInvalid ? appstr("exportFilenameInvalidChars") : undefined
          });
        }

        let requiresRebuild = false;
        if (
          updatedKey === "includeBackground" &&
          !nextFieldData.includeBackground &&
          nextFieldData.showActivity !== "Off"
        ) {
          nextFieldData.showActivity = "Off";
        }

        if (
          updatedKey === "scope" &&
          nextFieldData.scope !== "Page" &&
          nextFieldData.pageSize === "current"
        ) {
          nextFieldData.pageSize = getDefaultPageSize(sourceDocumentRef.current);
        }

        if (updatedKey === "scope") {
          requiresRebuild = scopeControllerRef.current?.onScopeChange(nextFieldData.scope) ?? false;
        }

        storeExportSettings("document", updatedKey, nextFieldData[updatedKey]);

        if (requiresRebuild) {
          return;
        }

        const handleUpdate = handlePreviewUpdate({
          mockDocumentRef,
          sourceDocumentRef,
          backgroundRef,
          nextFieldData,
          updatedKey
        });

        statusController.performUpdate(handleUpdate).then(() => {
          if (!mockDocumentRef.current) {
            return;
          }

          checkBuildLimit(mockDocumentRef.current, nextFieldData.scope);
        });
      }
    }
  );

  useStatusListener({
    statusController,
    listener: nextStatus => {
      if (nextStatus === "loading") {
        props.setDisabled("previewLoading", { isDisabled: true, tooltip: appstr("createpreview") });
      } else {
        props.setDisabled("previewLoading", {
          isDisabled: false
        });
      }
    }
  });

  useEffect(() => {
    const isInvalid = hasInvalidFilenameChars(formController.fieldData.name);
    props.setDisabled("invalidFilename", {
      isDisabled: isInvalid,
      tooltip: isInvalid ? appstr("exportFilenameInvalidChars") : undefined
    });
  }, []);

  useImperativeHandle(props.handleRef, () => ({
    handleExport
  }));

  const handleExport = async () => {
    const exporter = new Exporter();
    exportStartedRef.current = true;

    const mockDocument = mockDocumentRef.current!;
    const sourceDocument = sourceDocumentRef.current!;

    if (statusController.isReady) {
      await resetPreview(mockDocument, sourceDocument, formController.fieldData);
    } else {
      const wsMgr = GetWSMgr();
      wsMgr?.SetSuppressProgress(false);

      const scopeOptions = getScopeOptions(
        scopeControllerRef.current?.activeScope ?? "Page",
        sourceDocument
      );

      const [mockDoc] = await exporter.createMockDocument(
        sourceDocument,
        new AbortController(),
        scopeOptions
      );
      await handleSetup(mockDoc!, sourceDocument, backgroundRef, formController.fieldData);
    }

    return exporter.export({
      type: "document",
      fieldData: formController.fieldData,
      mode: props.mode
    });
  };

  const handlePostBuild = (doc: QXDoc) => {
    let effectiveScope = formController.fieldData["scope"];

    // Synchronize the inputs with the built scope
    if (scopeControllerRef.current) {
      const inputScope = formController.fieldData["scope"];
      effectiveScope = scopeControllerRef.current.activeScope;
      const scopeComparison = compareScope(scopeControllerRef.current.activeScope, inputScope);

      // If the active scope is more restrictive than the input scope, update the input scope
      // This can happen if the user has selected a scope that exceeds their multi-page limit
      if (scopeComparison > 0) {
        formController.onFieldChange("scope", scopeControllerRef.current.activeScope);
      }
    }

    checkBuildLimit(doc, effectiveScope);
  };

  const checkBuildLimit = (doc: QXDoc, scope: Scope) => {
    const docMgr = doc.GetDocMgr();
    const wsMgr = docMgr?.fWSMgr;
    const accountBoxLimit = wsMgr?.GetAllowedBoxCount() ?? 0;
    const recordCount = countExportableRecords(doc, scope);

    props.setDisabled("boxLimit", {
      isDisabled: recordCount > accountBoxLimit,
      tooltip: appstr(gBoxLimitKey)
    });
  };

  const handleReduceScope = (scope: Scope) => {
    formController.onFieldChange("scope", scope);
  };

  return (
    <Flex height="100%">
      <Flex flex={2}>
        <PreviewCanvas
          exportStartedRef={exportStartedRef}
          fieldData={formController.fieldData}
          backgroundRef={backgroundRef}
          mockDocumentRef={mockDocumentRef}
          scopeControllerRef={scopeControllerRef}
          onReduceScope={handleReduceScope}
          sourceDocumentRef={sourceDocumentRef}
          statusController={statusController}
          onPostBuild={handlePostBuild}
        />
      </Flex>
      <Flex flex={1}>
        <Tabs
          customClass={cx("ocn-chart-explorer-tabs", "oc-overflow-hidden")}
          customContentClass={cx("oc-overflow-auto", "ocn-list-scrollbar", "oc-fill-height")}
          tabIndex={currentTab}
          onTabChange={setCurrentTab}
          tabs={[
            {
              header: appstr("General"),
              dataTestId: "export-modal-tab-general",
              content: (
                <DocumentExportGeneral
                  scopeControllerRef={scopeControllerRef}
                  formController={formController}
                  isIncludeBackgroundDisabled={isADPBasicAccount}
                  mode={props.mode}
                />
              )
            },
            {
              header: appstr("Pages"),
              dataTestId: "export-modal-tab-pages",
              content: (
                <DocumentExportPages
                  formController={formController}
                  mode={props.mode}
                  sourceDocument={sourceDocumentRef.current}
                />
              )
            },
            {
              header: appstr("Advanced"),
              dataTestId: "export-modal-tab-advanced",
              content: <DocumentExportAdvanced formController={formController} mode={props.mode} />
            }
          ]}
        />
      </Flex>
    </Flex>
  );
};
